/**
 * Session log — a local ring buffer so "was it recorded?" is verifiable.
 *
 * Every load, staged edit, confirm and failure appends one entry. Shown under
 * Activity → "This session". Lives in localStorage; never sent anywhere.
 */

export type LogKind = "info" | "ok" | "warn" | "error";

export interface LogEntry {
  ts: string;
  kind: LogKind;
  text: string;
}

const KEY = "psychotp.session.log";
const MAX = 250;

let cache: LogEntry[] | null = null;

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function sessionLog(): LogEntry[] {
  if (cache) return cache;
  const raw = storage()?.getItem(KEY);
  if (!raw) {
    cache = [];
    return cache;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    cache = Array.isArray(parsed)
      ? parsed.filter(
          (e): e is LogEntry =>
            typeof e === "object" &&
            e !== null &&
            typeof (e as LogEntry).text === "string",
        )
      : [];
  } catch {
    cache = [];
  }
  return cache;
}

export function logEvent(kind: LogKind, text: string): void {
  const list = [...sessionLog(), { ts: new Date().toISOString(), kind, text }];
  cache = list.slice(-MAX);
  storage()?.setItem(KEY, JSON.stringify(cache));
}

export function clearLog(): void {
  cache = [];
  storage()?.removeItem(KEY);
}
